import React, { useEffect, useState, useMemo } from 'react';
import { Button, Stack } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useEffectOnce, useUpdateEffect } from 'usehooks-ts';
import DataTable from 'react-data-table-component';
import { TableColumn } from 'react-data-table-component';
import { compact } from 'lodash';

import { ActionRequest, ActionRequestStatuses } from '@models/ActionRequest';
import { formatDateTime } from '@utils/datetime';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil, faEye } from '@fortawesome/free-solid-svg-icons';

import {
  actionRequestsFetch,
  ActionRequestsFetchPayload,
  actionRequestCreate,
  ActionRequestCreatePayload,
  actionRequestUpdate,
  ActionRequestUpdatePayload,
  stateClear,
  StateUpdatePayload,
  stateUpdate,
} from './action';
import { selectFeatureActionRequest } from './selector';
import { ActionRequestFormModal, ActionRequestFormValues } from './action-request-form-modal';
import { ActionRequestDetailsModal } from './action-request-details-modal';
import { ActionRequestBadge } from './action-request-badge';

const customStyles = {
  headCells: {
    style: {
      fontWeight: 'bold',
    },
  },
};

type Props = Pick<ActionRequestsFetchPayload, 'subjectType' | 'subjectId'> & {
  canCreate?: boolean;
};

const ActionRequestTabContent = ({ subjectType, subjectId, canCreate = false }: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { actionRequests, isLoading, isReload, isSuccess, pagination } = useSelector(selectFeatureActionRequest);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [showFormDialog, setShowFormDialog] = useState(false);
  const [selectedActionRequest, setSelectedActionRequest] = useState<ActionRequest | null>(null);

  const columns = useMemo(() => compact([
    {
      cell: (actionRequest) => (
        <Stack direction="horizontal" gap={1}>
          {(canCreate && actionRequest.status === ActionRequestStatuses.Open) && (
            <Button
              onClick={() => handleEdit(actionRequest)}
              variant="outline-primary"
              size="sm"
            >
              <FontAwesomeIcon icon={faPencil} />
            </Button>
          )}

          <Button
            onClick={() => handleShow(actionRequest)}
            variant="outline-info"
            size="sm"
          >
            <FontAwesomeIcon icon={faEye} />
          </Button>
        </Stack>
      ),
      button: true,
      center: true,
    },
    {
      name: t('common:title'),
      selector: ({ title }) => title,
    },
    {
      name: t('common:status'),
      selector: ({ status }) => <ActionRequestBadge status={status} />,
    },
    {
      name: t('common:createdDate'),
      selector: ({ createdAt }) => formatDateTime(createdAt),
      minWidth: '180px',
    },
    {
      name: t('common:requester'),
      selector: ({ requester }) => requester.name,
    },
  ]) as TableColumn<ActionRequest>[], [canCreate]);

  const handleClose = () => {
    setSelectedActionRequest(null);
    setShowFormDialog(false);
    const payload: StateUpdatePayload = { isSuccess: false, serverErrors: {} };
    dispatch(stateUpdate(payload));
  };

  const handleEdit = (actionRequest: ActionRequest) => {
    setSelectedActionRequest(actionRequest);
    setShowFormDialog(true);
  };

  const handleShow = (actionRequest: ActionRequest) => {
    setSelectedActionRequest(actionRequest);
    setShowDetailsDialog(true);
  };

  useEffect(() => {
    if (isSuccess && showFormDialog) {
      handleClose();
    }
  }, [isSuccess]);

  useEffect(() => {
    const payload: ActionRequestsFetchPayload = { subjectType, subjectId, page, perPage };
    dispatch(actionRequestsFetch(payload));
  }, [subjectType, subjectId, page, perPage]);

  useEffectOnce(() => {
    return () => {
      dispatch(stateClear());
    };
  });

  useUpdateEffect(() => {
    const payload: ActionRequestsFetchPayload = { subjectType, subjectId, page: 1, perPage };
    dispatch(actionRequestsFetch(payload));
  }, [isReload]);

  return (
    <>
      <DataTable
        columns={columns}
        data={actionRequests ?? []}
        customStyles={customStyles}
        fixedHeader
        pagination
        paginationServer
        progressPending={isLoading}
        paginationPerPage={perPage}
        paginationTotalRows={pagination?.total ?? 0}
        onChangeRowsPerPage={(newPerPage) => setPerPage(newPerPage)}
        onChangePage={(newPage) => setPage(newPage)}
      />
      <Stack direction="horizontal" gap={2} className="mt-5">
        {canCreate && (
          <Button onClick={() => setShowFormDialog(true)} variant="outline-primary">
            Add Request
          </Button>
        )}
      </Stack>

      <ActionRequestFormModal
        actionRequest={selectedActionRequest}
        show={showFormDialog}
        onHide={handleClose}
        onSubmit={(values: ActionRequestFormValues) => {
          if (selectedActionRequest) {
            const payload: ActionRequestUpdatePayload = { id: selectedActionRequest.id, ...values };
            dispatch(actionRequestUpdate(payload));
          } else {
            const payload: ActionRequestCreatePayload = { subjectType, subjectId, ...values };
            dispatch(actionRequestCreate(payload));
          }
        }}
      />

      <ActionRequestDetailsModal
        actionRequest={selectedActionRequest}
        show={showDetailsDialog}
        onHide={() => {
          setSelectedActionRequest(null);
          setShowDetailsDialog(false);
        }}
      />
    </>
  );
};

export { ActionRequestTabContent };
